import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CpySelectField } from "@/shared/components/field/cpy-select-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { TextInputField } from "@/shared/components/field/input-field";
import { SourcesParams } from "@/services/backend/cpy/api/cpy";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import {
  OfferReferenceBase,
  OfferReferenceBasePatch,
} from "../types/offer-reference-base";

/**
 * OfferReferenceCard edits the reference of a document to an offer: the
 * offer number and date. Selecting an offer copies its data into the
 * document destinationId with the cpy operation from sourceEntityName,
 * listing the offers of sourcesParams. hideOfferSelect hides the select,
 * e.g. for a document no copy operation exists for.
 */
export function OfferReferenceCard({
  reference,
  onPatch,
  destinationId,
  destinationEntityName,
  sourceEntityName,
  sourcesParams,
  hideOfferSelect = false,
  description = t("Das Angebot, auf das sich das Dokument bezieht."),
  disabled,
}: {
  reference: OfferReferenceBase;
  onPatch: (patch: OfferReferenceBasePatch) => void;
  destinationId: UUID;
  destinationEntityName: CpyName;
  sourceEntityName: CpyName;
  sourcesParams: SourcesParams;
  hideOfferSelect?: boolean;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Angebot")}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        {!hideOfferSelect && (
          <CpySelectField
            label={t("Angebot")}
            description={t(
              "Die Angebotsnummer und das Angebotsdatum werden durch die Auswahl überschrieben.",
            )}
            warning=""
            value={reference.offerId}
            onValueReset={() => onPatch({ offerId: null })}
            destinationId={destinationId}
            destinationEntityName={destinationEntityName}
            sourceEntityName={sourceEntityName}
            sourcesParams={sourcesParams}
            fieldName="offerId"
            fieldProps={{ className: "lg:col-span-2" }}
            disabled={disabled}
          />
        )}
        <TextInputField
          label={t("Angebotsnummer")}
          text={reference.offerReference}
          onTextChange={(offerReference) => onPatch({ offerReference })}
          fieldName="offerReference"
          disabled={disabled}
        />
        <DatePickerField
          label={t("Angebotsdatum")}
          nullable={true}
          date={reference.offerDate}
          onDateChange={(date) => onPatch({ offerDate: date ?? null })}
          fieldName="offerDate"
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}
